import { IIamSettings } from "./auth/iamSettings";
import { DriveAPICredentialsActions } from "../store/driveAPICredentials/actions";
import { DriveStore } from "../store/reducers";
import { sanitizeUserName } from "./users";
import { extend } from "./vanilla";

export interface ICredentials {
    userName: string;
    database: string;
    sessionId?: string;
    iamJwt?: string;
    password?: string;
    externalUsername?: string;
    /**
     * Full OIDC token set for webview IAM logins (OIDC popup / sso.samlLogin path).
     * Populated by iamFlow.ts loginSuccessCallBack so the native layer can
     * create a restorable IAM session via sessionEstablished.
     * Not present for AppAuth / native-bridge IAM logins (those create a native
     * session directly via bridgeOidcLogin).
     */
    oidcRefreshToken?: string;
    oidcIdToken?: string;
    /** Unix timestamp in milliseconds when the access token expires. */
    oidcExpiresAt?: number;
}

export interface ICredentialsWithServer {
    credentials: ICredentials;
    server: string;
    iamSettings?: IIamSettings;
}

export interface ICredentialsStore {
    get: () => ICredentialsWithServer | null;
    set: (credentials: ICredentials, server: string, iamSettings?: IIamSettings) => void;
    clear: () => void;
}

export class DriveCredentialsStore implements ICredentialsStore {
    readonly store: DriveStore;
    private user: string;

    constructor(store: DriveStore) {
        this.store = store;
    }

    get userName() {
        return this.user;
    }

    set userName(userName: string) {
        this.user = sanitizeUserName(userName);
    }

    get(userName?: string) {
        let thisCredentials: ICredentialsWithServer | null = null;
        let credUserName = userName || this.user;
        const driveAPICredentials = this.store.getState().driveAPICredentials;
        const currentUserApiCredentials: DeepReadonlyObject<ICredentialsWithServer> | null = credUserName && driveAPICredentials && driveAPICredentials[sanitizeUserName(credUserName)] || null;

        if (currentUserApiCredentials) {
            thisCredentials = extend(true, {}, currentUserApiCredentials);
            if (thisCredentials && typeof thisCredentials.server !== "string") {
                // something went wrong and server format is invalid. set server to the current host to try and proceed.
                thisCredentials.server = window.location.host;

                if (thisCredentials.credentials && thisCredentials.server) {
                    this.set(thisCredentials.credentials, thisCredentials.server, thisCredentials.iamSettings);
                }
            }
        } else {
            // No credentials, senor!
            return null;
        }
        return thisCredentials;
    }

    set(credentials: ICredentials, server: string, iamSettings?: IIamSettings): void {
        this.user = sanitizeUserName(credentials.userName);

        if (credentials?.database) {
            credentials.database = credentials.database.toLowerCase();
        }

        // Add or update (if already there) the new user's credentials
        this.store.dispatch(DriveAPICredentialsActions.setUserCredentials({ userName: this.user, credentials, server, iamSettings }));
    }

    clear() {
        this.store.dispatch(DriveAPICredentialsActions.clearUserCredentials(this.user));
    }
}

/**
 * A credentials store that is not connected to the redux store.
 */
export class SandboxedCredentialsStore implements ICredentialsStore {
    private storedCredentials: ICredentialsWithServer | null = null;
    get(): ICredentialsWithServer | null {
        return this.storedCredentials;
    }
    set(credentials: ICredentials, server: string): void {
        this.storedCredentials = { credentials, server };
    }
    clear(): void {
        this.storedCredentials = null;
    }
}
